import React, { useEffect, useRef, useState } from "react";
import Icons from "../icons";
import { statusOf } from "../testStatus";
import { stepToEnglish, stepToScript } from "../stepEnglish";
import { DIALOG_HEIGHT } from "./dialogSize";

/* Circular status glyph (per step). */
function StatusGlyph({ status, className = "h-4 w-4" }) {
  const s = statusOf(status);
  const Glyph = s.icon;
  return (
    <span
      className={[
        "inline-flex flex-none items-center justify-center rounded-full",
        s.solid,
      ].join(" ")}
    >
      <Glyph className={[className, status === "running" ? "animate-spin" : ""].join(" ")} />
    </span>
  );
}

function StatusBadge({ status }) {
  const s = statusOf(status);
  const Glyph = s.icon;
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        s.badge,
      ].join(" ")}
    >
      <Glyph className={["h-3.5 w-3.5", status === "running" ? "animate-spin" : ""].join(" ")} />
      {s.label}
    </span>
  );
}

/* Full-screen viewer for a single step's screenshot, with prev/next nav. */
function ScreenshotLightbox({ src, caption, onClose, onPrev, onNext, hasPrev, hasNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && hasPrev) onPrev();
      else if (e.key === "ArrowRight" && hasNext) onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Prev — outside the screenshot, on the left. */}
      <button
        type="button"
        onClick={onPrev}
        disabled={!hasPrev}
        aria-label="Previous screenshot"
        className="relative z-10 mr-3 flex-none rounded-full bg-slate-800/80 p-2.5 text-white shadow-lg ring-1 ring-slate-600 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-25"
      >
        <Icons.ChevronLeft className="h-6 w-6" />
      </button>

      <div className="relative z-10 flex max-h-full min-w-0 max-w-6xl flex-col overflow-hidden rounded-xl bg-slate-900 shadow-2xl ring-1 ring-slate-700">
        <div className="flex items-center justify-between gap-3 border-b border-slate-700 px-4 py-2 text-sm text-slate-200">
          <span className="truncate">{caption}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close screenshot"
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-black">
          <img src={src} alt={caption} className="mx-auto block h-auto max-w-full" />
        </div>
      </div>

      {/* Next — outside the screenshot, on the right. */}
      <button
        type="button"
        onClick={onNext}
        disabled={!hasNext}
        aria-label="Next screenshot"
        className="relative z-10 ml-3 flex-none rounded-full bg-slate-800/80 p-2.5 text-white shadow-lg ring-1 ring-slate-600 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-25"
      >
        <Icons.ChevronRight className="h-6 w-6" />
      </button>
    </div>
  );
}

/* Renders the validation-error strings the app displayed after a primary-
 * action click. Populated from the `errors_detected` WebSocket event
 * emitted by the runner when its post-click DOM scan finds visible
 * error containers. */
function DetectedErrorsList({ errors }) {
  if (!errors || !errors.length) return null;
  return (
    <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 p-2">
      <div className="mb-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-rose-700">
        App rejected — {errors.length} validation error{errors.length === 1 ? "" : "s"} visible
      </div>
      <ul className="space-y-1">
        {errors.map((e, idx) => (
          <li key={idx} className="text-[0.6875rem] text-rose-800">
            <span className="mr-1">•</span>
            {e.field && (
              <span className="font-medium">{e.field}</span>
            )}
            {e.field && <span className="text-rose-500"> · </span>}
            <span>“{e.text}”</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* One step entry on the right-hand column. */
function StepItem({ step, index, active, onView }) {
  const s = statusOf(step.status);
  const border =
    step.status === "passed"
      ? "border-emerald-200"
      : step.status === "failed"
      ? "border-rose-300"
      : step.status === "running"
      ? "border-amber-300"
      : "border-slate-200";
  return (
    <li
      className={[
        "rounded-lg border bg-card p-3 transition",
        border,
        active ? "ring-2 ring-brand-400" : "",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-slate-100 text-[0.6875rem] font-semibold text-slate-600">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusGlyph status={step.status} className="h-3.5 w-3.5" />
            <span className={["text-sm font-medium", s.text].join(" ")}>{s.label}</span>
            {step.action && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.625rem] uppercase tracking-wider text-slate-600">
                {step.action}
              </span>
            )}
          </div>

          {/* What the step does, in plain English — then the actual Playwright
              instruction underneath. The sentence is what a reviewer reads;
              the instruction is what you debug a stale selector with. */}
          <p className="mt-1 text-sm text-slate-700">{stepToEnglish(step)}</p>
          {stepToScript(step) && (
            <p
              className="mt-0.5 break-all font-mono text-[0.6875rem] leading-snug text-slate-400"
              title="The Artifact instruction this step runs"
            >
              ({stepToScript(step)})
            </p>
          )}

          {/* Badge showing where the value came from (datasource vs prompt)
              and the resolved value so users can verify what actually ran.
              Masked steps (e.g. passwords) hide the value. */}
          {step.resolvedSource && (
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[0.6875rem]">
              <span
                className={[
                  "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold uppercase tracking-wide ring-1 ring-inset",
                  step.resolvedSource === "datasource"
                    ? "bg-blue-50 text-blue-700 ring-blue-600/20"
                    : "bg-amber-50 text-amber-700 ring-amber-600/20",
                ].join(" ")}
              >
                {step.resolvedSource === "datasource" ? "dataset" : "prompt"}
              </span>
              <span className="font-mono text-slate-600">
                →{" "}
                {step.resolvedMasked
                  ? "••••••••"
                  : step.value != null && step.value !== ""
                  ? String(step.value)
                  : <span className="italic text-slate-400">(empty)</span>}
              </span>
            </p>
          )}

          {step.detectedErrors && (
            <DetectedErrorsList errors={step.detectedErrors} />
          )}

          {step.error && (
            <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 font-mono text-[0.6875rem] text-rose-700">
              {step.error}
            </p>
          )}

          {/* View the screenshot in the left panel (in place of the live view). */}
          {step.screenshot && (
            <button
              type="button"
              onClick={() => onView?.(index)}
              className={[
                "mt-2 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition",
                active
                  ? "border-brand-500 bg-brand-50 text-brand-700"
                  : "border-slate-200 bg-card text-slate-600 hover:border-brand-500 hover:text-brand-700",
              ].join(" ")}
              title="Show screenshot on the left"
            >
              <Icons.Eye className="h-3.5 w-3.5" />
              View screenshot
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/* Modal that asks the tester for a dynamic value mid-run.
 * Rendered on top of the run view so the live browser frame stays visible
 * as context for what value to supply. */
export function DynamicPromptModal({ prompt, onSubmit }) {
  const [value, setValue] = useState("");
  const [visible, setVisible] = useState(false);
  const inputRef = React.useRef(null);

  // Reset when a new prompt arrives (e.g. next dynamic step).
  useEffect(() => {
    setValue("");
    setVisible(false);
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [prompt?.index]);

  const submit = (e) => {
    e?.preventDefault?.();
    onSubmit(value);
  };

  const label = prompt.field_label || prompt.param_name || "value";
  const isPassword = !!prompt.masked || prompt.kind === "password";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div className={`relative z-10 flex w-full max-w-sm flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex-none border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">Value needed to continue</h3>
          <p className="mt-1 text-xs text-slate-500">
            Step {prompt.index} paused — supply a value for
            <span className="mx-1 rounded bg-amber-50 px-1.5 py-0.5 font-mono text-[0.6875rem] text-amber-800 ring-1 ring-amber-200">
              {prompt.param_name || label}
            </span>
            to resume the run.
          </p>
        </div>
        <form onSubmit={submit} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4" autoComplete="off">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              {label}
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                type={isPassword && !visible ? "password" : "text"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={[
                  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25",
                  isPassword ? "pr-10" : "",
                ].join(" ")}
              />
              {isPassword && (
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setVisible((v) => !v)}
                  title={visible ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-2 flex items-center rounded p-1 text-slate-400 hover:text-slate-700"
                >
                  <Icons.Eye className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Modal for the auto-login credentials prompt. Fires once per run when the
 * runner hits its login step and the picked datasource doesn't supply
 * `_login_user` / `_login_password`. */
export function CredentialsPromptModal({ onSubmit }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pwdVisible, setPwdVisible] = useState(false);
  const userRef = React.useRef(null);

  useEffect(() => {
    const t = setTimeout(() => userRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!username.trim() || !password) return;
    onSubmit(username.trim(), password);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div className={`relative z-10 flex w-full max-w-sm flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex-none border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">Login credentials</h3>
          <p className="mt-1 text-xs text-slate-500">
            This is an auto-login test case. Enter the credentials to sign in and continue the run.
            Tip: add <span className="rounded bg-slate-100 px-1 font-mono text-[0.625rem]">_login_user</span> and{" "}
            <span className="rounded bg-slate-100 px-1 font-mono text-[0.625rem]">_login_password</span> to a
            datasource to skip this prompt.
          </p>
        </div>
        <form onSubmit={submit} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4" autoComplete="off">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              User ID
            </label>
            <input
              ref={userRef}
              name="cq-runner-userid"
              autoComplete="off"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Password
            </label>
            <div className="relative">
              <input
                type={pwdVisible ? "text" : "password"}
                name="cq-runner-pwd"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-10 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setPwdVisible((v) => !v)}
                title={pwdVisible ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-2 flex items-center rounded p-1 text-slate-400 hover:text-slate-700"
              >
                <Icons.Eye className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="submit"
              disabled={!username.trim() || !password}
              className="inline-flex items-center justify-center rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-60"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Full-page run viewer. Replaces the board table (same pattern as the create
 * pages). Left column streams the live browser video — or, when the user
 * clicks a step's "View screenshot", that step's captured screenshot (with
 * prev/next + maximize). Right column lists each step.
 */
export default function RunView({
  testCase,
  frameUrl,
  isRunning,
  isPaused,
  onBack,
  onRun,
  onStop,
  onPause,
  onResume,
  onHistory,
  pendingPrompt,
  onSubmitPrompt,
  pendingCredentials,
  onSubmitCredentials,
}) {
  // Global step index currently shown in the left panel, or null for live view.
  const [selectedStep, setSelectedStep] = useState(null);
  // Whether the selected screenshot is also opened full-screen (maximized).
  const [maximized, setMaximized] = useState(false);
  // Whether the whole run view (live browser + steps panel) is expanded to
  // full-screen. Independent of `maximized` above (which only affects the
  // per-step screenshot lightbox).
  const [pageMaximized, setPageMaximized] = useState(false);
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);

  // Escape exits page-fullscreen mode.
  useEffect(() => {
    if (!pageMaximized) return undefined;
    const onKey = (e) => e.key === "Escape" && setPageMaximized(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pageMaximized]);

  // When a fresh run starts, snap back to the live view.
  const prevRunning = useRef(isRunning);
  useEffect(() => {
    if (isRunning && !prevRunning.current) {
      setSelectedStep(null);
      setMaximized(false);
    }
    prevRunning.current = isRunning;
  }, [isRunning]);

  if (!testCase) return null;

  const steps = testCase.steps || [];
  const total = steps.length;
  const done = steps.filter((s) => s.status === "passed" || s.status === "failed").length;
  const passed = steps.filter((s) => s.status === "passed").length;

  // Steps that have a screenshot, in order — the set we page through.
  const shots = steps
    .map((s, i) => ({ i, src: s.screenshot, caption: `Step ${i + 1}: ${s.name}` }))
    .filter((x) => x.src);

  const pos = selectedStep == null ? -1 : shots.findIndex((x) => x.i === selectedStep);
  const current = pos >= 0 ? shots[pos] : null; // null => show live view
  const hasPrev = pos > 0;
  const hasNext = pos >= 0 && pos < shots.length - 1;
  const goPrev = () => hasPrev && setSelectedStep(shots[pos - 1].i);
  const goNext = () => hasNext && setSelectedStep(shots[pos + 1].i);
  const showLive = () => {
    setSelectedStep(null);
    setMaximized(false);
  };

  return (
    <div
      className={
        pageMaximized
          ? "fixed inset-0 z-40 flex flex-col overflow-hidden bg-slate-50 p-4 sm:p-6"
          : ""
      }
    >
      {/* Header */}
      <div className={pageMaximized ? "mb-4 flex-none" : "mb-6"}>
        <button
          type="button"
          onClick={onBack}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
        >
          <Icons.ArrowLeft className="h-4 w-4" />
          Back to test suite
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold text-slate-900">{testCase.name}</h1>
              <span className="flex-none rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.6875rem] font-medium text-slate-500">
                {testCase.priority}
              </span>
              <StatusBadge status={testCase.status} />
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {done} / {total} step{total === 1 ? "" : "s"} run · {passed} passed
              {testCase.createdBy ? ` · by ${testCase.createdBy}` : ""}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPageMaximized((m) => !m)}
              title={pageMaximized ? "Restore (Esc)" : "Expand to full screen"}
              aria-label={pageMaximized ? "Restore" : "Maximize"}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-800"
            >
              <Icons.Maximize className="h-4 w-4" />
            </button>
            {onHistory && (
              <button
                type="button"
                onClick={() => onHistory(testCase)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <Icons.Clock className="h-4 w-4" />
                History
              </button>
            )}
            {isRunning && (isPaused ? (
              <button
                type="button"
                onClick={() => onResume?.()}
                title="Resume execution from where it paused"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              >
                <Icons.Play className="h-4 w-4" />
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onPause?.()}
                title="Pause between the current step and the next"
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-600"
              >
                <Icons.Pause className="h-4 w-4" />
                Pause
              </button>
            ))}
            {isRunning && (
              <button
                type="button"
                onClick={() => setStopConfirmOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700"
              >
                <Icons.X className="h-4 w-4" />
                Stop
              </button>
            )}
            <button
              type="button"
              onClick={() => !isRunning && onRun?.(testCase.id)}
              disabled={isRunning || total === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRunning ? (
                <>
                  <Icons.Spinner className="h-4 w-4 animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <Icons.Refresh className="h-4 w-4" />
                  Run again
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Two-column body: video/screenshot (left) + steps (right) */}
      <div
        className={[
          "grid grid-cols-1 gap-4 lg:grid-cols-2",
          pageMaximized ? "min-h-0 flex-1" : "",
        ].join(" ")}
      >
        {/* Left — live video OR the selected step's screenshot */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-sm">
          <div className="flex flex-none items-center justify-between gap-2 border-b border-slate-700 px-4 py-2 text-slate-100">
            {current ? (
              <>
                <div className="flex min-w-0 items-center gap-2">
                  <Icons.Eye className="h-4 w-4 flex-none text-slate-300" />
                  <span className="truncate text-sm font-semibold" title={current.caption}>
                    {current.caption}
                  </span>
                </div>
                <div className="flex flex-none items-center gap-1">
                  {isRunning && (
                    <button
                      type="button"
                      onClick={showLive}
                      className="mr-1 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-500"
                    >
                      <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-white" />
                      Back to live
                    </button>
                  )}
                  <span className="mr-1 text-xs text-slate-400">
                    {pos + 1} / {shots.length}
                  </span>
                  <button
                    type="button"
                    onClick={goPrev}
                    disabled={!hasPrev}
                    aria-label="Previous screenshot"
                    className="rounded-md p-1 text-slate-300 hover:bg-slate-800 hover:text-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Icons.ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={!hasNext}
                    aria-label="Next screenshot"
                    className="rounded-md p-1 text-slate-300 hover:bg-slate-800 hover:text-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Icons.ChevronRight className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setMaximized(true)}
                    aria-label="Maximize screenshot"
                    title="Maximize"
                    className="rounded-md p-1 text-slate-300 hover:bg-slate-800 hover:text-slate-100"
                  >
                    <Icons.Maximize className="h-5 w-5" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex h-2 w-2 flex-none rounded-full ${
                      isRunning ? "animate-pulse bg-emerald-400" : "bg-slate-500"
                    }`}
                  />
                  <span className="text-sm font-semibold">Live execution</span>
                </div>
                <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-300">
                  {isRunning ? "Live" : "Ended"}
                </span>
              </>
            )}
          </div>
          <div
            className={[
              "relative flex flex-1 items-center justify-center overflow-hidden bg-black",
              pageMaximized ? "min-h-0" : "min-h-[20rem] lg:min-h-[70vh]",
            ].join(" ")}
          >
            {current ? (
              <img
                src={current.src}
                alt={current.caption}
                draggable={false}
                className="max-h-full max-w-full select-none object-contain"
              />
            ) : frameUrl ? (
              <img
                src={frameUrl}
                alt="Live browser view"
                draggable={false}
                className="max-h-full max-w-full select-none object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 px-6 text-center text-slate-400">
                {isRunning ? (
                  <>
                    <Icons.Spinner className="h-6 w-6 animate-spin" />
                    <p className="text-xs font-medium">Waiting for first frame…</p>
                  </>
                ) : (
                  <>
                    <Icons.Eye className="h-6 w-6" />
                    <p className="text-xs font-medium">
                      No live video. Click “Run again” to watch the test execute, or review the
                      captured screenshots on the right.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right — steps */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
          <div className="flex flex-none items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <h2 className="text-sm font-semibold text-slate-900">Steps</h2>
            <span className="text-xs text-slate-500">
              {done} / {total} run
            </span>
          </div>
          <div
            className={[
              "min-h-0 flex-1 overflow-auto p-4",
              pageMaximized ? "" : "lg:max-h-[70vh]",
            ].join(" ")}
          >
            {steps.length === 0 ? (
              <p className="text-sm text-slate-500">No steps in this test case.</p>
            ) : (
              <ol className="space-y-2">
                {steps.map((step, i) => (
                  <StepItem
                    key={i}
                    step={step}
                    index={i}
                    active={selectedStep === i}
                    onView={setSelectedStep}
                  />
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>

      {maximized && current && (
        <ScreenshotLightbox
          src={current.src}
          caption={current.caption}
          onClose={() => setMaximized(false)}
          onPrev={goPrev}
          onNext={goNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />
      )}

      {pendingPrompt && onSubmitPrompt && (
        <DynamicPromptModal prompt={pendingPrompt} onSubmit={onSubmitPrompt} />
      )}

      {pendingCredentials && onSubmitCredentials && (
        <CredentialsPromptModal onSubmit={onSubmitCredentials} />
      )}

      {stopConfirmOpen && (
        <StopConfirmModal
          onCancel={() => setStopConfirmOpen(false)}
          onConfirm={() => {
            setStopConfirmOpen(false);
            onStop?.(testCase.id);
          }}
        />
      )}
    </div>
  );
}

/* Confirmation dialog fired by the Stop button. Explicit "are you sure"
 * gate so a mistaken click doesn't wipe an in-flight run. */
function StopConfirmModal({ onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white shadow-xl ring-1 ring-slate-200">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">Stop this run?</h3>
          <p className="mt-1 text-sm text-slate-600">
            The currently-executing step and every step after it will be marked
            as skipped. You'll be taken back to the steps view.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            autoFocus
            className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Keep running
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center justify-center rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
          >
            Yes, stop
          </button>
        </div>
      </div>
    </div>
  );
}
